'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useScopedI18n } from '@locales/client';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import styled from 'styled-components';

import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { Image } from '@/shared/ui/image/Image';
import { Link } from '@/shared/ui/link';
import { BodyTextMediumC } from '@/shared/ui/typography2';

import { getInfoTabPath } from '@/shared/libs/get-info-tab-path';

import type { InfoMenuItem, InfoTab } from './config';

type InfoSectionMenuClientProps = {
  initialTab: InfoTab;
  items: InfoMenuItem[];
  locale: string;
};

export const InfoSectionMenuClient = ({
  initialTab,
  items,
  locale,
}: InfoSectionMenuClientProps) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const t = useScopedI18n('common.footer');
  const router = useRouter();
  const getItemHref = (item: InfoMenuItem) =>
    `/${locale}${getInfoTabPath(item.id)}`;

  if (isDesktop) {
    return (
      <InfoMenu>
        {items.map((item) => (
          <MenuItem
            key={`${item.id}_${item.value}`}
            $isSelected={item.id === initialTab}
          >
            <Link href={getItemHref(item)}>
              <BodyTextMediumC>{t(item.labelKey)}</BodyTextMediumC>
            </Link>
          </MenuItem>
        ))}
      </InfoMenu>
    );
  }

  return (
    <>
      <MenuButton onClick={() => setIsMobileMenuOpen(true)}>
        <BodyTextMediumC>{t('select_page')}</BodyTextMediumC>
        <Image
          src="/images/icons/search-icon.svg"
          width={24}
          height={24}
          alt="search"
        />
      </MenuButton>
      <Dialog.Root open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <Dialog.Portal>
          <MobileInfoMenuOverlay />

          <MobileInfoMenuContainer
            aria-describedby=""
            data-item="info_section_menu"
          >
            <CloseButton onClick={() => setIsMobileMenuOpen(false)}>
              <Image src="/images/icons/close-dialog.svg" alt="close" />
            </CloseButton>
            <MobileInfoMenu>
              {items.map((item) => {
                const isSelected = item.id === initialTab;
                return (
                  <MobileMenuItem
                    key={item.value}
                    $isSelected={isSelected}
                    onClick={() => {
                      router.push(getItemHref(item));
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <BodyTextMediumC>{t(item.labelKey)}</BodyTextMediumC>
                    {isSelected && (
                      <Image
                        src="/images/icons/check.svg"
                        alt="check"
                        style={{ marginLeft: 'auto' }}
                      />
                    )}
                  </MobileMenuItem>
                );
              })}
            </MobileInfoMenu>
          </MobileInfoMenuContainer>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

const InfoMenu = styled.nav`
  display: flex;
  width: 220px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  border-radius: 8px;
  background: #27262b;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MobileInfoMenuContainer = styled(Dialog.Content)`
  display: flex;
  position: fixed;
  top: 0;
  flex-direction: column;
  justify-content: space-between;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  padding: 79px 18px 64px 18px;
`;

const MobileInfoMenu = styled(InfoMenu)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  background: transparent;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MobileInfoMenuOverlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  background: rgba(19, 19, 21, 0.65);
  backdrop-filter: blur(18px);
  z-index: 1;
`;

const MenuItem = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  position: relative;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  cursor: pointer;

  border-radius: 8px;
  background-color: ${({ $isSelected }) =>
    $isSelected ? '#4b444c' : 'transparent'};
  transition: background-color 0.3s ease;

  & > a {
    padding: 12px;
    display: flex;
    width: 100%;
    height: 100%;
  }
`;

const MobileMenuItem = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  position: relative;
  padding: 12px;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  cursor: pointer;
  transition: background-color 0.3s ease;
  background-color: ${({ $isSelected }) =>
    $isSelected ? '#4b444c' : '#27262b'};

  border-radius: 0;

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const MenuButton = styled.button`
  display: flex;
  height: 24px;
  padding: 16px;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  border-radius: 8px;
  background: #27262b;
`;

const CloseButton = styled.button`
  all: unset;
  display: flex;
  align-self: flex-end;
`;
